import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Plus, Search, ArrowLeft, MapPin, Phone, Users, 
  Edit2, Trash2, X, Shield
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { securityAPI } from '../services/api';

interface Caseta {
  id: string;
  name: string;
  location: string;
  phone: string;
  guards: string[];
  status: 'active' | 'inactive';
}

// Mock data removed — page fetches real data from securityAPI

export default function CasetasVigilancia() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [casetas, setCasetas] = useState<Caseta[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [editingCaseta, setEditingCaseta] = useState<Caseta | null>(null);

  const { isAdmin } = useAuth();

  useEffect(() => {
    loadCasetas();
  }, []);

  const loadCasetas = async () => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      const response = await securityAPI.getCasetas(condoId);
      const data = response.data || response || [];
      setCasetas(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading casetas:', err);
      setCasetas([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('¿Eliminar esta caseta?')) {
      setCasetas(prev => prev.filter(c => c.id !== id));
    }
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-white border-b border-gray-200 z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] hover:bg-gray-100 rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#2D3748]">Casetas de Vigilancia</h1>
              <p className="text-xs text-gray-500">Gestión de puntos de seguridad</p>
            </div>
          </div>
          {isAdmin() && (
            <button 
              onClick={() => setShowNewModal(true)}
              className="px-4 py-2 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Nueva Caseta
            </button>
          )}
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto">
        <div className="grid gap-4">
          {casetas.map(caseta => (
            <div key={caseta.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Shield className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-[#2D3748]">{caseta.name}</h3>
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${caseta.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                        {caseta.status === 'active' ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 flex items-center gap-1 mb-1">
                      <MapPin className="w-4 h-4" /> {caseta.location}
                    </p>
                    <p className="text-sm text-gray-500 flex items-center gap-1 mb-2">
                      <Phone className="w-4 h-4" /> {caseta.phone}
                    </p>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {caseta.guards.length > 0 ? caseta.guards.join(', ') : 'Sin personal asignado'}
                      </span>
                    </div>
                  </div>
                </div>
                {isAdmin() && (
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setEditingCaseta(caseta)}
                      className="p-2 text-gray-400 hover:text-[#36CCD8] hover:bg-[#36CCD8]/10 rounded-xl"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => handleDelete(caseta.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      {(showNewModal || editingCaseta) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#2D3748] mb-6">{editingCaseta ? 'Editar' : 'Nueva'} Caseta</h3>
            <form className="space-y-4">
              <input type="text" placeholder="Nombre" defaultValue={editingCaseta?.name} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
              <input type="text" placeholder="Ubicación" defaultValue={editingCaseta?.location} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
              <input type="tel" placeholder="Teléfono" defaultValue={editingCaseta?.phone} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
              <div className="flex gap-3">
                <button type="button" onClick={() => { setShowNewModal(false); setEditingCaseta(null); }} className="flex-1 py-3 border border-gray-200 rounded-xl">Cancelar</button>
                <button type="button" onClick={() => { setShowNewModal(false); setEditingCaseta(null); alert('✅ Guardado'); }} className="flex-1 py-3 bg-[#36CCD8] text-white rounded-xl">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
